import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
export const BASE_URL = process.env.REACT_APP_BASE_URL;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  sendOTP: (mobile) => api.post('/auth/send-otp', { mobile }),
  verifyOTP: (mobile, otp) => api.post('/auth/verify-otp', { mobile, otp }),
  login: (mobile, password) => api.post('/auth/login', { mobile, password }),
  getProfile: () => api.get('/auth/me'),
  registerCustomer: (data) => api.post('/auth/register', data),
  updateProfile: (data) => api.put('/auth/profile', data)
};

// Product API calls
export const productAPI = {
  getProducts: (params) => api.get('/products', { params }),
  getProductById: (id) => api.get(`/products/${id}`),
  getProductBrands: (id) => api.get(`/products/${id}/brands`),
  getCategories: () => api.get('/products/categories'),
  getCategoryById: (id) => api.get(`/products/categories/${id}`),
  getTypes: () => api.get('/products/types'),
  getTypesByCategory: (category_id) => api.get(`/products/types/category/${category_id}`),
  getGlobalBrands: () => api.get('/products/global-brands'),
  getBrandProducts: (id) => api.get(`/products/global-brands/${id}/products`)
};

// Quote API calls
export const quoteAPI = {
  createQuote: (data) => api.post('/quotes', data),
  getQuotes: (params) => api.get('/quotes', { params }),
  getQuoteById: (id) => api.get(`/quotes/${id}`)
};

// Order API calls
export const orderAPI = {
  createOrder: (data) => {
    // Handle FormData for payment proof upload
    if (data instanceof FormData) {
      return api.post('/orders', data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return api.post('/orders', data);
  },
  createDirectOrder: (data) => {
    // Handle FormData for payment proof upload
    if (data instanceof FormData) {
      return api.post('/orders/direct', data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return api.post('/orders/direct', data);
  },
  getOrders: (params) => api.get('/orders', { params }),
  getOrderById: (id) => api.get(`/orders/${id}`)
};

// Finance API calls
export const financeAPI = {
  submitFinanceRequest: (formData) => {
    return api.post('/finance', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getFinanceRequests: () => api.get('/finance'),
  getFinanceRequestById: (id) => api.get(`/finance/${id}`),
  // Finance Documents API
  uploadFinanceDocuments: (formData) => {
    return api.post('/finance/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getFinanceDocuments: () => api.get('/finance/documents')
};

// Tender API calls
export const tenderAPI = {
  getTenders: (params) => api.get('/tenders', { params }),
  getTenderById: (id) => api.get(`/tenders/${id}`)
};

// Notification API calls
export const notificationAPI = {
  getNotifications: (page = 1, limit = 20) => api.get(`/notifications?page=${page}&limit=${limit}`),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  deleteNotification: (id) => api.delete(`/notifications/${id}`)
};

export const contactAPI = {
  submitQuery: (data) => api.post('/contact', data)
};

export const siteSettingsAPI = {
  getSettings: () => api.get('/settings')
};

export const testimonialAPI = {
  getActive: () => api.get('/testimonials/active')
};

export default api;
